import React, { useCallback, useEffect, useState } from 'react';
import {
  DocumentArrowDownIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { Factura } from './types';
import { formatearMoneda, formatearFechaConHora } from '../../utils/formatters';
import { metodoPagoFacturaLabel } from '../../utils/facturaPdfHtml';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  generateWhatsAppReceiptMessage,
  openWhatsAppWithReceipt,
  getClientPhoneForWhatsApp,
  generateAndUploadReceipt,
} from '../../utils/reciboPagoService';

interface HistorialPagosPanelProps {
  factura: Factura;
  companyName: string;
  /** When true, start expanded */
  defaultExpanded?: boolean;
}

type PagoUnificado = {
  id: string;
  monto: number;
  metodo: string;
  notas: string | null;
  reciboUrl: string | null;
  comprobanteUrl: string | null;
  fecha: string;
  origen: 'pagos_factura' | 'pagos_cobro' | 'pagos_credito' | 'historial';
  tablaRecibo: 'pagos_factura' | 'pagos_cobro' | 'pagos_credito' | null;
};

export function HistorialPagosPanel({
  factura,
  companyName,
  defaultExpanded = false,
}: HistorialPagosPanelProps) {
  const { currentEmpresa } = useAuthStore();
  const [pagos, setPagos] = useState<PagoUnificado[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [regenId, setRegenId] = useState<string | null>(null);
  const clientPhone = getClientPhoneForWhatsApp(factura);

  const loadPagos = useCallback(async () => {
    if (!factura.id) return;
    setLoading(true);
    try {
      const unified: PagoUnificado[] = [];

      const { data: pf } = await supabase
        .from('pagos_factura')
        .select('id, monto, metodo_pago, notas, recibo_url, created_at')
        .eq('factura_id', factura.id)
        .order('created_at', { ascending: false });

      for (const p of pf || []) {
        unified.push({
          id: p.id,
          monto: Number(p.monto) || 0,
          metodo: p.metodo_pago || '—',
          notas: p.notas,
          reciboUrl: p.recibo_url,
          comprobanteUrl: null,
          fecha: p.created_at,
          origen: 'pagos_factura',
          tablaRecibo: 'pagos_factura',
        });
      }

      const { data: cobros } = await supabase
        .from('cobros')
        .select('id')
        .eq('factura_id', factura.id);

      const cobroIds = (cobros || []).map((c) => c.id);
      if (cobroIds.length > 0) {
        let pc:
          | Array<{
              id: string;
              monto: number;
              metodo_pago: string | null;
              notas: string | null;
              recibo_url?: string | null;
              comprobante_url?: string | null;
              created_at: string;
              fecha_pago?: string | null;
            }>
          | null = null;

        const withComp = await supabase
          .from('pagos_cobro')
          .select('id, monto, metodo_pago, notas, recibo_url, comprobante_url, created_at, fecha_pago')
          .in('cobro_id', cobroIds)
          .order('created_at', { ascending: false });

        if (withComp.error) {
          const without = await supabase
            .from('pagos_cobro')
            .select('id, monto, metodo_pago, notas, recibo_url, created_at, fecha_pago')
            .in('cobro_id', cobroIds)
            .order('created_at', { ascending: false });
          pc = without.data;
        } else {
          pc = withComp.data;
        }

        for (const p of pc || []) {
          if (unified.some((u) => u.id === p.id)) continue;
          unified.push({
            id: p.id,
            monto: Number(p.monto) || 0,
            metodo: p.metodo_pago || '—',
            notas: p.notas,
            reciboUrl: p.recibo_url || null,
            comprobanteUrl: p.comprobante_url || null,
            fecha: p.fecha_pago || p.created_at,
            origen: 'pagos_cobro',
            tablaRecibo: 'pagos_cobro',
          });
        }
      }

      const { data: creditos } = await supabase
        .from('creditos')
        .select('id')
        .eq('factura_id', factura.id);

      const creditoIds = (creditos || []).map((c) => c.id);
      if (creditoIds.length > 0) {
        const withComp = await supabase
          .from('pagos_credito')
          .select('id, monto, metodo_pago, notas, recibo_url, comprobante_url, fecha_pago')
          .in('credito_id', creditoIds)
          .order('fecha_pago', { ascending: false });

        const pc = withComp.error
          ? (
              await supabase
                .from('pagos_credito')
                .select('id, monto, metodo_pago, notas, fecha_pago')
                .in('credito_id', creditoIds)
                .order('fecha_pago', { ascending: false })
            ).data
          : withComp.data;

        for (const p of pc || []) {
          if (unified.some((u) => u.id === p.id)) continue;
          unified.push({
            id: p.id,
            monto: Number(p.monto) || 0,
            metodo: p.metodo_pago || '—',
            notas: p.notas,
            reciboUrl: (p as { recibo_url?: string | null }).recibo_url || null,
            comprobanteUrl: (p as { comprobante_url?: string | null }).comprobante_url || null,
            fecha: p.fecha_pago,
            origen: 'pagos_credito',
            tablaRecibo: 'pagos_credito',
          });
        }
      }

      // Overlay comprobante/recibo from historial_pagos; add cobro/crédito rows if the table insert did not stick
      if (factura.historialPagos?.length) {
        const teniaFilasTabla = unified.length > 0;
        factura.historialPagos.forEach((h, idx) => {
          const orig = String(h.origen || '').toLowerCase();
          const match = unified.find(
            (u) =>
              (u.origen === 'pagos_cobro' || u.origen === 'pagos_credito') &&
              Math.round(u.monto) === Math.round(h.monto)
          );
          if (match) {
            if (!match.comprobanteUrl && h.comprobanteUrl) match.comprobanteUrl = h.comprobanteUrl;
            if (!match.reciboUrl && h.reciboUrl) match.reciboUrl = h.reciboUrl;
            return;
          }
          if (orig !== 'cobro' && orig !== 'credito' && teniaFilasTabla) return;
          unified.push({
            id: `hist-${idx}`,
            monto: h.monto,
            metodo: h.metodo,
            notas: h.notas || null,
            reciboUrl: h.reciboUrl || null,
            comprobanteUrl: h.comprobanteUrl || null,
            fecha: h.fecha instanceof Date ? h.fecha.toISOString() : String(h.fecha),
            origen: 'historial',
            tablaRecibo: null,
          });
        });
      }

      // Gap: monto_pagado en factura > suma de abonos listados (pago al crear / sin detalle)
      const sumListed = unified.reduce((s, p) => s + p.monto, 0);
      const pagadoFactura = Math.round(Number(factura.montoPagado) || 0);
      const pagoPrevio = Math.max(0, pagadoFactura - sumListed);
      if (pagoPrevio > 0.009) {
        unified.push({
          id: 'pago-previo',
          monto: pagoPrevio,
          metodo: factura.metodoPago || '—',
          notas: 'Pago registrado con la factura (sin recibo de abono aparte)',
          reciboUrl: null,
          comprobanteUrl: null,
          fecha: factura.fechaCreacion
            ? factura.fechaCreacion instanceof Date
              ? factura.fechaCreacion.toISOString()
              : String(factura.fechaCreacion)
            : new Date(0).toISOString(),
          origen: 'historial',
          tablaRecibo: null,
        });
      }

      unified.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
      setPagos(unified);
    } catch (e) {
      console.warn('HistorialPagosPanel load:', e);
    } finally {
      setLoading(false);
    }
  }, [
    factura.id,
    factura.historialPagos,
    factura.montoPagado,
    factura.metodoPago,
    factura.fechaCreacion,
  ]);

  useEffect(() => {
    if (expanded) void loadPagos();
  }, [expanded, loadPagos]);

  useEffect(() => {
    if (defaultExpanded) void loadPagos();
  }, [defaultExpanded, loadPagos]);

  const handleOpenReceipt = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSendWhatsApp = (pago: PagoUnificado, saldoDespues: number) => {
    if (!clientPhone || !pago.reciboUrl) return;
    const reciboNumero = `REC-${pago.id.slice(0, 8).toUpperCase()}`;
    const message = generateWhatsAppReceiptMessage(
      factura,
      reciboNumero,
      pago.monto,
      saldoDespues,
      pago.reciboUrl,
      companyName
    );
    openWhatsAppWithReceipt(clientPhone, message, '57');
  };

  const handleRegenerateReceipt = async (pago: PagoUnificado) => {
    if (!currentEmpresa?.id || !pago.tablaRecibo) return;
    setRegenId(pago.id);
    try {
      // Approximate balances from sorted payments (oldest first)
      const chronological = [...pagos].sort(
        (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
      );
      let running = factura.total;
      let saldoAnterior = factura.total;
      let saldoDespues = factura.total;
      for (const p of chronological) {
        saldoAnterior = running;
        running = Math.max(0, running - p.monto);
        if (p.id === pago.id) {
          saldoDespues = running;
          break;
        }
      }

      const result = await generateAndUploadReceipt(
        factura,
        {
          pagoId: pago.id,
          monto: pago.monto,
          metodoPago: pago.metodo,
          saldoAnterior,
          saldoDespues,
          notas: pago.notas || undefined,
        },
        {
          empresaId: currentEmpresa.id,
          companyName: currentEmpresa.nombre || companyName,
          companyLogo: currentEmpresa.logo || null,
          companyEmail: currentEmpresa.email || '',
          companyPhone: currentEmpresa.telefono || '',
          companyAddress: currentEmpresa.direccion || '',
        },
        pago.tablaRecibo
      );

      setPagos((prev) =>
        prev.map((p) => (p.id === pago.id ? { ...p, reciboUrl: result.reciboUrl || p.reciboUrl } : p))
      );

      if (result.reciboUrl) {
        window.open(result.reciboUrl, '_blank', 'noopener,noreferrer');
      } else if (result.pdfBlob) {
        const url = URL.createObjectURL(result.pdfBlob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (e) {
      console.warn('Regenerar recibo:', e);
      alert('No se pudo generar el recibo. Intenta de nuevo.');
    } finally {
      setRegenId(null);
    }
  };

  const hasAny =
    pagos.length > 0 ||
    (factura.historialPagos && factura.historialPagos.length > 0) ||
    (factura.montoPagado ?? 0) > 0;

  if (!hasAny && !loading) {
    return null;
  }

  // Saldo después: partimos del total y restamos en orden cronológico
  // (incluye el "pago previo" sintético para que no diga 750k cuando el saldo real es 150k)
  const pagosAsc = [...pagos].sort(
    (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
  );
  const saldoDespuesPorId: Record<string, number> = {};
  {
    let run = Math.round(Number(factura.total) || 0);
    for (const p of pagosAsc) {
      run = Math.max(0, run - p.monto);
      saldoDespuesPorId[p.id] = run;
    }
  }

  const pagosDisplay = [...pagosAsc].reverse(); // más reciente primero

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden print:border-gray-400">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors print:bg-white"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Historial de pagos / recibos</span>
          <span className="text-xs bg-[#1A56DB] text-white px-2 py-0.5 rounded-full">
            {pagos.length || factura.historialPagos?.length || '…'}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">Cargando historial...</div>
          ) : pagos.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-500">
              No hay abonos registrados con detalle. Pagado acumulado:{' '}
              {formatearMoneda(factura.montoPagado ?? 0)}.
            </div>
          ) : (
            <>
              <div className="px-4 py-2 bg-slate-50 text-[11px] text-gray-600">
                Total {formatearMoneda(factura.total)} · Pagado {formatearMoneda(factura.montoPagado ?? 0)} ·
                Saldo {formatearMoneda(
                  factura.montoPendiente ??
                    Math.max(0, factura.total - (factura.montoPagado ?? 0))
                )}
              </div>
              {pagos.some((p) => p.id === 'pago-previo') && (
                <div className="px-4 py-2 bg-amber-50 text-[11px] text-amber-900 border-b border-amber-100">
                  El total <strong>Pagado</strong> incluye un pago al crear la factura (sin recibo aparte).
                  Solo los abonos posteriores generan recibo descargable.
                </div>
              )}
              {pagosDisplay.map((pago) => {
              const saldoDespues = saldoDespuesPorId[pago.id] ?? 0;
              const esPrevio = pago.id === 'pago-previo';
              return (
                <div key={pago.id} className="px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-green-600">
                          +{formatearMoneda(pago.monto)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {esPrevio ? 'Pago inicial / previo' : metodoPagoFacturaLabel(pago.metodo)}
                        </span>
                        {esPrevio && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            Sin recibo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {esPrevio ? 'Al crear o antes de los abonos con recibo' : formatearFechaConHora(pago.fecha)}
                      </p>
                      {pago.notas && (
                        <p className="text-xs text-gray-600 mt-1 italic">{pago.notas}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Saldo después: {formatearMoneda(saldoDespues)}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {esPrevio ? (
                        <span className="text-[11px] text-gray-400 italic">Pago en la factura</span>
                      ) : (
                        <>
                          {pago.comprobanteUrl ? (
                            <button
                              type="button"
                              onClick={() => handleOpenReceipt(pago.comprobanteUrl!)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-md"
                              title="Ver comprobante del cliente"
                            >
                              Comprobante
                            </button>
                          ) : null}
                          {pago.reciboUrl ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleOpenReceipt(pago.reciboUrl!)}
                                className="p-1.5 text-gray-500 hover:text-[#1A56DB] hover:bg-blue-50 rounded-md"
                                title="Ver / descargar recibo"
                              >
                                <DocumentArrowDownIcon className="w-4 h-4" />
                              </button>
                              {clientPhone && (
                                <button
                                  type="button"
                                  onClick={() => handleSendWhatsApp(pago, saldoDespues)}
                                  className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md"
                                  title="Enviar por WhatsApp"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                  </svg>
                                </button>
                              )}
                            </>
                          ) : pago.tablaRecibo ? (
                            <button
                              type="button"
                              onClick={() => void handleRegenerateReceipt(pago)}
                              disabled={regenId === pago.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#1A56DB] hover:bg-blue-50 rounded-md disabled:opacity-50"
                              title="Generar recibo de este abono"
                            >
                              {regenId === pago.id ? (
                                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                              )}
                              Recibo
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
